import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Check, X, Shield, Filter, RefreshCw, UserCheck, UserX, Ban, Clock, Activity, Mail
} from 'lucide-react';
import toast from 'react-hot-toast';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    isVerified: '',
    schoolId: ''
  });
  const [schools, setSchools] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserStatus, setEditingUserStatus] = useState(null);
  const [userStatus, setUserStatus] = useState('active');
  const [suspensionDays, setSuspensionDays] = useState(1);
  const [statusReason, setStatusReason] = useState('');
  const [sendingVerification, setSendingVerification] = useState(null);
  const [sendingBulkVerification, setSendingBulkVerification] = useState(false);

  useEffect(() => {
    // Fetch schools for the filter dropdown
    const fetchSchools = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/schools`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch schools');
        }

        const data = await response.json();
        setSchools(data);
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    };

    fetchSchools();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        // Construct query params
        const queryParams = new URLSearchParams({
          page,
          limit,
          ...(search && { search }),
          ...(filters.role && { role: filters.role }),
          ...(filters.isVerified !== '' && { isVerified: filters.isVerified }),
          ...(filters.schoolId && { schoolId: filters.schoolId })
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/users?${queryParams.toString()}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data.users);
        setTotalUsers(data.total);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, limit, search, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      isVerified: '',
      schoolId: ''
    });
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/role`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: newRole })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      // Update user in state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));

      toast.success(`User role updated to ${newRole}`);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const getSchoolName = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    return school ? school.name : 'Unknown School';
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'moderator':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    }
  };

  // Add status badge function
  const getUserStatusBadge = (status) => {
    switch(status) {
      case 'suspended':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
            <Clock size={14} className="mr-1" /> Suspended
          </span>
        );
      case 'banned':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
            <Ban size={14} className="mr-1" /> Banned
          </span>
        );
      case 'active':
      default:
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            <Activity size={14} className="mr-1" /> Active
          </span>
        );
    }
  };

  // Add function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(parseInt(timestamp)).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Add send verification function
  const sendVerificationToUser = async (userId) => {
    try {
      setSendingVerification(userId);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/send-verification`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification email');
      }

      toast.success(data.message || 'Verification email sent successfully');
    } catch (error) {
      console.error('Error sending verification email:', error);
      toast.error(error.message || 'Failed to send verification email');
    } finally {
      setSendingVerification(null);
    }
  };

  // Add bulk verification function
  const sendBulkVerification = async () => {
    try {
      setSendingBulkVerification(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/send-bulk-verification`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send bulk verification');
      }

      toast.success(data.message || 'Bulk verification emails sent successfully');
    } catch (error) {
      console.error('Error sending bulk verification:', error);
      toast.error(error.message || 'Failed to send bulk verification emails');
    } finally {
      setSendingBulkVerification(false);
    }
  };

  // Add update user status function
  const updateUserStatus = async (userId) => {
    try {
      // Validate inputs
      if (userStatus === 'suspended' && (!suspensionDays || suspensionDays < 1)) {
        toast.error('Please specify a valid suspension duration');
        return;
      }
      
      if ((userStatus === 'suspended' || userStatus === 'banned') && !statusReason.trim()) {
        toast.error('Please provide a reason for this action');
        return;
      }
      
      // Prepare data in the format expected by the backend
      const statusData = {
        status: userStatus,
        reason: statusReason.trim() || null,
        days: userStatus === 'suspended' ? parseInt(suspensionDays, 10) : null
      };

      setLoading(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(statusData)
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user status');
      }

      // Calculate the expiration timestamp for suspensions
      const statusUntil = userStatus === 'suspended' && suspensionDays 
        ? Date.now() + (parseInt(suspensionDays, 10) * 24 * 60 * 60 * 1000) 
        : null;
      
      // Update the user in state after successful API call
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { 
          ...user, 
          status: userStatus, 
          statusUntil: statusUntil,
          statusReason: statusReason.trim() || null,
          statusChangedAt: Date.now(),
        } : user
      ));

      // Reset form and show success message
      setEditingUserStatus(null);
      toast.success(data.message || `User status updated to ${userStatus}`);
      
      // Optional: Refresh the users list to get the latest data from server
      // fetchUsers();
      
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalUsers / limit);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage all users on the EDX Red platform
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={sendBulkVerification}
            disabled={sendingBulkVerification}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingBulkVerification ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Mail size={16} className="mr-2" />
                Send Bulk Verification
              </>
            )}
          </button>
          <div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-sm">
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Users</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-white">{totalUsers}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <form onSubmit={handleSearch} className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full pl-10 p-2.5"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X size={18} className="text-gray-400 hover:text-gray-500" />
                </button>
              )}
            </form>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
              >
                <option value="">All Roles</option>
                <option value="admin">Admins</option>
                <option value="moderator">Moderators</option>
                <option value="student">Students</option>
              </select>
            </div>
            
            <div>
              <select
                value={filters.isVerified}
                onChange={(e) => handleFilterChange('isVerified', e.target.value)}
                className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
              >
                <option value="">All Verification Status</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
            </div>
            
            <div>
              <select
                value={filters.schoolId}
                onChange={(e) => handleFilterChange('schoolId', e.target.value)}
                className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5"
              >
                <option value="">All Schools</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </div>
            
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-300"
            >
              <RefreshCw size={16} className="mr-2" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    School
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Last Verification
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.length > 0 ? (
                  users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {getSchoolName(user.schoolId)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser === user.id ? (
                          <div className="flex items-center space-x-2">
                            <select
                              value={user.role}
                              onChange={(e) => {
                                const newRole = e.target.value;
                                setUsers(users.map(u => 
                                  u.id === user.id ? { ...u, role: newRole } : u
                                ));
                              }}
                              className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-xs rounded-lg focus:ring-red-500 focus:border-red-500 p-1"
                            >
                              <option value="student">Student</option>
                              <option value="moderator">Moderator</option>
                              <option value="admin">Admin</option>
                            </select>
                            <button
                              onClick={() => updateUserRole(user.id, user.role)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => setEditingUser(null)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(user.role)}`}>
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUserStatus === user.id ? (
                          <div className="flex flex-col space-y-2">
                            <select
                              value={userStatus}
                              onChange={(e) => setUserStatus(e.target.value)}
                              className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-xs rounded-lg focus:ring-red-500 focus:border-red-500 p-1.5 w-full"
                            >
                              <option value="active">Active</option>
                              <option value="suspended">Suspended</option>
                              <option value="banned">Banned</option>
                            </select>

                            {userStatus === 'suspended' && (
                              <div className="flex items-center">
                                <label className="text-xs text-gray-500 dark:text-gray-400 mr-2">Days:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="365"
                                  value={suspensionDays}
                                  onChange={(e) => setSuspensionDays(e.target.value)}
                                  className="w-16 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-xs rounded-lg focus:ring-red-500 focus:border-red-500 p-1"
                                />
                              </div>
                            )}

                            <input
                              type="text"
                              value={statusReason}
                              onChange={(e) => setStatusReason(e.target.value)}
                              placeholder="Reason"
                              className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-xs rounded-lg focus:ring-red-500 focus:border-red-500 p-1.5 w-full"
                            />

                            <div className="flex space-x-2 justify-end">
                              <button
                                onClick={() => updateUserStatus(user.id)}
                                className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                              >
                                <Check size={12} className="mr-1" /> Save
                              </button>
                              <button
                                onClick={() => setEditingUserStatus(null)}
                                className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <X size={12} className="mr-1" /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {getUserStatusBadge(user.status || 'active')}
                            {user.status === 'suspended' && user.statusUntil && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Until {formatDate(user.statusUntil)}
                              </div>
                            )}
                            {user.statusReason && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs" title={user.statusReason}>
                                Reason: {user.statusReason}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.lastVerification ? formatDate(user.lastVerification) : 'Never'}
                        </div>
                        {['student', 'moderator'].includes(user.role) && (
                          <div className="text-xs mt-1">
                            {user.lastVerification && Date.now() - user.lastVerification < (6 * 30 * 24 * 60 * 60 * 1000) ? (
                              <span className="text-green-600 dark:text-green-400">✓ Current</span>
                            ) : (
                              <span className="text-amber-600 dark:text-amber-400">⚠ Needs verification</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {['student', 'moderator'].includes(user.role) && (
                          <button
                            onClick={() => sendVerificationToUser(user.id)}
                            disabled={sendingVerification === user.id}
                            className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 mr-3 disabled:opacity-50"
                          >
                            {sendingVerification === user.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 inline mr-1"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <Mail size={16} className="inline mr-1" />
                                Send Verification
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingUserStatus(user.id);
                            setUserStatus(user.status || 'active');
                            setSuspensionDays(1);
                            setStatusReason('');
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                        >
                          Manage Status
                        </button>
                        <button
                          onClick={() => setEditingUser(user.id)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-3"
                        >
                          Edit Role
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No users found matching your criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 ${
                    page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 ${
                    page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * limit, totalUsers)}</span> of{' '}
                    <span className="font-medium">{totalUsers}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 ${
                        page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      &larr;
                    </button>
                    
                    {/* Page Numbers */}
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNumber = i + 1;
                      // Only show a few pages at a time
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= page - 1 && pageNumber <= page + 1)
                      ) {
                        return (
                          <button
                            key={i}
                            onClick={() => setPage(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border ${
                              page === pageNumber
                                ? 'z-10 bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-500 text-red-600 dark:text-red-400'
                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                            } text-sm font-medium`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        (pageNumber === 2 && page > 3) ||
                        (pageNumber === totalPages - 1 && page < totalPages - 2)
                      ) {
                        return (
                          <span
                            key={i}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 ${
                        page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      &rarr;
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsersPage;